import { Fragment, HTMLAttributes, useMemo } from "react";
import styles from "./breadcrumbs.module.scss";
import { DropdownMenu } from "../DropdownMenu";

interface BreadcrumbProps extends HTMLAttributes<HTMLDivElement> {
  levels: {
    label: string;
    path: string;
  }[];
  onNavigate: (path: string) => void;
  maxItems?: number;
}

export function Breadcrumbs({
  levels,
  onNavigate,
  maxItems = 4,
}: BreadcrumbProps) {
  const [visibleLevels, hiddenLevels] = useMemo(() => {
    if (levels.length <= maxItems) {
      return [levels.map((l, i) => ({ ...l, originalIndex: i })), []];
    }

    // Always show first item
    const first = { ...levels[0], originalIndex: 0 };
    // Always show last 2 items (parent of current, and current) as long as the maxItems is 3 or more
    const tailLength = maxItems >= 3 ? 2 : 1;
    const tail = levels
      .slice(-tailLength)
      .map((l, i) => ({ ...l, originalIndex: levels.length - tailLength + i }));
    // Get list of all the hidden elements
    const hidden = levels.slice(1, levels.length - tailLength);

    // Insert ellipsis in between
    const ellipsis = {
      label: "...",
      path: "",
      isEllipsis: true,
      originalIndex: -1,
    };

    return [[first, ellipsis, ...tail], hidden];
  }, [levels, maxItems]);

  return (
    <nav aria-label="breadcrumb" className={styles.breadcrumbs}>
      <ol>
        {visibleLevels.map((level, index) => {
          const isEllipsis = "isEllipsis" in level;
          const isLast = index === visibleLevels.length - 1;
          const isActuallyCurrent =
            level.originalIndex === levels.length - 1 && !isEllipsis;

          return (
            <Fragment key={isEllipsis ? "ellipsis" : level.originalIndex}>
              <li style={isEllipsis ? { minWidth: 16 } : {}}>
                {isEllipsis ? (
                  <DropdownMenu
                    id="ellipsis-dropdown"
                    activator={<span className={styles.label}>...</span>}
                    menuOptions={hiddenLevels.map((l) => ({
                      label: l.label,
                      onSelect: () => onNavigate(l.path),
                    }))}
                  />
                ) : (
                  <button
                    className={isActuallyCurrent ? styles.currentLevel : ""}
                    role="link"
                    aria-current={isActuallyCurrent}
                    onClick={() => onNavigate(level.path)}
                    title={level.label} // tooltip for full text on hover
                  >
                    <span className={`truncate ${styles.label}`}>
                      {level.label}
                    </span>
                  </button>
                )}
              </li>
              {!isLast && (
                <span aria-hidden className="no-select">
                  /
                </span>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
